import { useState, useEffect } from "react";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AiFillCaretRight, AiOutlineCaretDown, AiOutlineCloudDownload } from "react-icons/ai";
import { RiSendPlaneLine } from "react-icons/ri";
import { toast, Toaster } from "react-hot-toast";
import moment from "moment";
import { formatRut } from "rutlib";

import { statusColors, statusTranslator } from "../../../helpers/functions";
import { collaboratorTestsSelector, useCollaboratorsActions } from "../../../services/actions/collaborators.actions";
import { FolderInfo } from "../../components/folder-info/folder-info";
import DropdownActions from "../../../components/dropdown/dropdown-actions";
import { HOST_PUBLIC } from "../../../utils/axios";
import LocalTable from "../../../components/table-local/index";
import FolderContentComponent from "./components/folder-content";
import EditCollaboratorModal from "./components/edit-modal";
import { userAtom } from "../../../services/atoms/auth.atoms";
import { AVAILABLE_TESTS, ERROR_MESSAGES } from "../../../helpers/constants";
import { TestTypeService } from "../../../utils/test-type-registry";
import InvitationModal from "../clients-details/components/invitation-modal";
import IAIcon from '../../../assets/creation.png';
import IaInfoModal from "./components/ia-info-modal";
import IaSummaryModal from "./components/ia-summary-modal";
import AnswersModal from "./components/answers-modal";
import { useAuthActions } from "../../../services/actions/auth.actions";
import { useAnswersAction } from "../../../services/actions/answers.action";
import { answersAtom } from "../../../services/atoms/answers.atom";

const initialCollaboratorData = {
  name: "",
  surname: "",
  rut: "",
  email: "",
  area: "",
  position: "",
}

const badgeAccessor = (row) => (
  <div className={`gm-badge gm-badge--${statusColors(row)}`}>
    <span>{statusTranslator(row)}</span>
  </div>
);

const dateFotmatter = (date) => <div>{date ? `${moment(date).format('DD-MM-YYYY HH:mm')} hrs` : '-'}</div>;

const options = [
  { id: 'opt-1', title: 'Descargar Informe', action: 'download', icon: AiOutlineCloudDownload },
  { id: 'opt-2', title: 'Enviar Invitación', action: 'send', icon: RiSendPlaneLine },
  // { id: 'opt-3', title: 'Editar Colaborador', action: 'edit', icon: HiOutlinePencil },
];

const optionsAdminOperative = [
  { id: 'opt-2', title: 'Enviar Invitación', action: 'send', icon: RiSendPlaneLine },
];

const columns = [
  // { Header: 'ID', accessor: 'id', disableSortBy: true },
  { Header: 'Evaluación', accessor: 'testName', disableSortBy: true },
  { Header: 'Estado', accessor: ({ statusId }) => badgeAccessor(statusId), disableSortBy: true },
  { Header: 'Notificación', accessor: ({ sentAt }) => dateFotmatter(sentAt), disableSortBy: true },
  { Header: 'Realizado el', accessor: ({ finishedAt }) => dateFotmatter(finishedAt) },
];

const CollaboratorDetails = () => {
  const user = useRecoilValue(userAtom);
  const answersState = useRecoilValue(answersAtom);
  let { id: collaboratorId } = useParams();
  let navigate = useNavigate();
  let { state } = useLocation();
  const { buildIaSummary } = useCollaboratorsActions();
  const { refreshToken } = useAuthActions();
  const { updateCollaborator } = useCollaboratorsActions();
  const { getAnswers, resetAnswers } = useAnswersAction();
  const [open, setOpen] = useState(false);
  const [test, setTest] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showIaInfoModal, setShowIaInfoModal] = useState(false);
  const [showIaSummaryModal, setShowIaSummaryModal] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [collaborator, setCollaborator] = useState(initialCollaboratorData);
  const [isInternal, setInternal] = useState(true);
  const [iaSummary, setIaSummary] = useState("");
  const [mustBeShowGrowmeIaAction, setMustBeShowGrowmeIaAction] = useState("");
  const [mustBeShowAnswersAction, setMustBeShowAnswersAction] = useState(false);

  const collaboratorTests = useRecoilValueLoadable(collaboratorTestsSelector(state?.collaborator?.id || collaboratorId));

  const goBack = () => navigate(-1);
  const handleOptionAction = (e) => {
    if (e === 'edit') setOpen(true);
    if (e === 'download') {
      if (!test?.id) {
        toast.error('El informe aún no está disponible. Recarga la página e inténtalo de nuevo.');
        return;
      }
      window.open(`${HOST_PUBLIC}/${test.id}.pdf`, '_blank');
    }
    if (e === 'send') {
      setShowInvitationModal(true);
    }
  };

  const handleOpenInvitationModal = () => {
    setShowInvitationModal(!showInvitationModal);
  }

  const handleOpenIaInfoModal = () => {
    setShowIaInfoModal(!showIaInfoModal);
  }
  
  const handleOpenIaSummaryModal = () => {
    setShowIaSummaryModal(!showIaSummaryModal);
  }

  const handleOpenModal = () => setOpen(!open);

  useEffect(() => {
    setCollaborator((prevValues) => ({ ...prevValues, ...collaboratorTests.contents }));
    if (collaboratorTests.contents?.tests) {
      const test = collaboratorTests.contents.tests.find((t) => (TestTypeService.isTestCategory(t, 'competency') || TestTypeService.isTestCategory(t, 'unified')) && t.results);
      const isCompetencyTest = !!test?.results;
      setShowIaInfoModal(isCompetencyTest);
      setMustBeShowGrowmeIaAction(isCompetencyTest);
      setMustBeShowAnswersAction(isCompetencyTest && user?.data?.isSuperAdmin);
      const testWithResults = collaboratorTests.contents.tests.find(t => t.results) || collaboratorTests.contents.tests[0];
      handleOpenDetails(testWithResults);
    }
  }, [collaboratorTests.state, collaboratorTests.contents]);

  const handleSubmitEdit = (e) => {
    e.preventDefault();
    e.preventDefault();
    const dataToSend={
      ...collaborator,
      collaboratorInternal: isInternal,
      collaboratorExternal: !isInternal, 
      rut: formatRut(collaborator.rut),
      phone: collaborator.phone.length >3 ? `+56${collaborator.phone}` : ""
    }
    updateCollaborator(dataToSend, () => {
      toast.success('Colaborador actualizado exitosamente');
      handleOpenModal();
    });
  }

  const getYearsInCompany = (startDate) => {
    if (!startDate) return '';
    const years = moment().diff(moment(startDate), 'years');
    return `${years} ${years === 1 ? 'año' : 'años'} en la empresa`;
  };

  const isLessThanOneYear = (date) => {
    if (!date) return false;
    const years = moment().diff(moment(date), 'years');
    return years < 1;
  };

  const getTestDisc = () => {
    const testDiscAvailable = AVAILABLE_TESTS.find((test) => test.code === 'TestDisc');
    if (testDiscAvailable && collaboratorTests.contents?.tests) {
      return collaboratorTests.contents?.tests.find((test) => test.testId === testDiscAvailable.id);
    }
    return null;
  };

  const handleOpenDetails = (testSelected) => {
    let test = testSelected;
    if (TestTypeService.isTestCategory(testSelected, 'competency')) {
      const testDisc = getTestDisc();
      if (testDisc && testDisc.createdAt) {
        const testDiscDate = moment(testDisc.createdAt);
        const lessThanOneYear = isLessThanOneYear(testDiscDate);
        if (lessThanOneYear) {
          test = { ...testSelected, testDisc };
        }
      }
    }
    setTest(test);
  };

  const onClickGrowmeIa = () => {
    getIaSummaryAction();
  };

  const getIaSummaryAction = () => {
    const testForSummary = getTestForIaSummary();
    if (testForSummary) {
      setShowIaSummaryModal(true);
      getIaSummary(testForSummary);
    } else {
      toast.error('El colaborador no tiene pruebas disponibles con resultados para generar el resumen.');
    }
  }
  
  const onClickStartIaSummary = () => {
    setShowIaInfoModal(false);
    getIaSummaryAction();
  };

  // Prioritize unified test for IA summary, fallback to legacy Growme
  const getTestForIaSummary = () => {
    if (!collaboratorTests.contents?.tests) return null;
    // Prioritize unified test
    const unifiedTest = collaboratorTests.contents.tests.find(t => TestTypeService.isTestCategory(t, 'unified') && t.results);
    if (unifiedTest) return unifiedTest;
    // Fallback to competency test
    return collaboratorTests.contents.tests.find(t => TestTypeService.isTestCategory(t, 'competency') && t.results);
  };

  const getIaSummary = (test) => {
    setIaSummary("Cargando resumen...");
    buildIaSummary({
      collaboratorId: collaboratorId,
      collaboratorTestId: test.id,
    }, (res) => {
      if (res === 500) {
        toast.error('Error al obtener el resumen IA');
        return;
      }
      if (res === 401) {
        toast.error(ERROR_MESSAGES.session_expired);
        refreshToken();
        return;
      }
      setIaSummary(res.analysis || "Ocurrió un problema al obtener el resumen. Intenten más tarde.");
    });
  };

  const onSeeAnswersClick = () => {
    resetAnswers();
    setShowAnswersModal(true);
    getAnswers({
      collaboratorTestId: test?.id,
    });
  };

  const yearsInCompany = getYearsInCompany(collaborator?.startDate);

  const modalItems = collaboratorTests?.contents ? [collaboratorTests?.contents] : [];

  const handleToggleAnswersModal = () => {
    setShowAnswersModal(false);
  };

  return (
    <div className="gm-collaborator-details-container">
      <Toaster />
      <EditCollaboratorModal
        collaboratorHandler={{
          collaborator,
          setCollaborator,
          isInternal,
          setInternal,
        }}
        open={open}
        submit={handleSubmitEdit}
        toggle={handleOpenModal}
      />
      {state?.client && (
        <div className="gm-cd-top-navigation">
          <div className="gm-cd-top-info">
            {user?.data.isSuperAdmin && (
              <div className="gm-cd-disabled" type="button">
                Lista Clientes
                <>
                  <AiFillCaretRight />
                </>
              </div>
            )}
            {state?.client?.name && (
              <>
                <button onClick={goBack} className="gm-cd-back">{`${
                  state?.client?.name
                }: ${state?.client?.sector || "No Informada"}`}</button>
              </>
            )}
            {collaborator?.name && (
              <>
                <AiFillCaretRight />
                <div className="gm-cd-company-info">{`${
                  collaborator?.name || ""
                } ${collaborator?.surname || ""}`}</div>
              </>
            )}
          </div>
          <div className="gm-actions-dropdown">
            <DropdownActions
              options={user?.data?.isAdminBasic ? optionsAdminOperative : options}
              label="Acciones"
              handleOnClick={handleOptionAction}
              Icon={AiOutlineCaretDown}
            />
            {
              mustBeShowGrowmeIaAction &&
              <button
                className="gm-btn-ia"
                type="button"
                onClick={onClickGrowmeIa}>
                Growme IA
                <img src={IAIcon} alt="IA icon" />
              </button>
            }
          </div>
        </div>
      )}
      {state?.charge && (
        <div className="gm-cd-top-navigation">
          <div className="gm-cd-top-info">
            {state?.charge && (
              <>
                <button
                  onClick={goBack}
                  className="gm-cd-back"
                >{`${state?.charge}`}</button>
              </>
            )}
            {collaborator?.name && (
              <>
                <AiFillCaretRight />
                <div className="gm-cd-company-info">{`${
                  collaborator?.name || ""
                } ${collaborator?.surname || ""}`}</div>
              </>
            )}
          </div>
          <div className="gm-actions-dropdown">
            <DropdownActions
              options={user?.data?.isAdminBasic ? optionsAdminOperative : options}
              label="Acciones"
              handleOnClick={handleOptionAction}
              Icon={AiOutlineCaretDown}
            />
          </div>
        </div>
      )}
      <FolderInfo
        title={`${collaborator?.name || ""} ${collaborator?.surname || ""} • ${collaborator.area} • ${yearsInCompany}`}
        ContentComponent={
          <FolderContentComponent
            test={test}
            collaborator={
              collaboratorTests.state === "hasValue"
                ? collaboratorTests.contents
                : collaborator
            }
            mustBeShowAnswersAction={mustBeShowAnswersAction}
            onSeeAnswersClick={onSeeAnswersClick}
          />
        }
      />
      <LocalTable
        columns={columns}
        rowSelected={test}
        handleOpenDetails={handleOpenDetails}
        emptyListInformer="small"
        section={{ plural: "tests", singular: "test" }}
        data={
          collaboratorTests.state === "hasValue"
            ? collaboratorTests.contents?.tests || []
            : []
        }
        loading={collaboratorTests.state === "loading"}
      />
      <InvitationModal
        open={showInvitationModal}
        toggle={handleOpenInvitationModal}
        checkedItems={modalItems}
        allItems={[]}
      />
      <IaInfoModal
        open={showIaInfoModal}
        toggle={handleOpenIaInfoModal}
        onClickStartIaSummary={onClickStartIaSummary}
      />
      <IaSummaryModal
        open={showIaSummaryModal}
        toggle={handleOpenIaSummaryModal}
        iaSummary={iaSummary}
      />
      <AnswersModal
        open={showAnswersModal}
        toggle={handleToggleAnswersModal}
        answers={answersState.answers}
        loading={answersState.loading}
      />
    </div>
  );
}

export default CollaboratorDetails;
